import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { ApplicationTable } from 'components/Tables/ApplicationTable';

import { ApplicationListDropFilterFields } from 'components/ApplicationList/ApplicationListDropFilterFields';
import { Loading } from '@leapeasy/ui-kit';
import { openDetails } from 'store/actions/uiActions';
import { getUserAppAction } from 'store/actions/userActions';
import { getUserApp } from 'store/sagas/APIs/user';
import { failedNotif } from 'store/actions/notifActions';
import {
  buildScopedBuildingBreadcrumbParent,
  withBreadcrumbParents,
} from 'utils/breadcrumbNavigation';
import { useListQueryState } from 'hooks/useListQueryState';
import { useClampListPagination } from 'hooks/useClampListPagination';
import { fetchAllPaginated } from 'utils/exportPagination';
import {
  ApplicationListActiveFilters,
  buildApplicationListFilterChips,
  parseApplicationListFilterChipId,
} from 'components/ApplicationListActiveFilters/ApplicationListActiveFilters';
import { getApplicationPrefilterByKey } from 'utils/applicationPrefilters';
import {
  APPLICATION_LIST_URL_PARAM_KEYS,
  APPLICATION_LIST_URL_PARAM_KEYS_WITH_PREFILTER,
  DEFAULT_APPLICATION_LIST_EXTRA_FILTERS,
  areArraysEqual,
  areApplicationCreatedDateRangeFiltersEqual,
  areDateRangesEqual,
  buildApplicationListRequestFilter,
  normalizeArrayFilter,
  normalizeApplicationCreatedDateRangeFilter,
  normalizeLeaseDateFilter,
  parseApplicationsFilters,
  serializeApplicationsFilters,
} from 'utils/applicationListQueryFilters';
import { EMPTY_APPLICATION_CREATED_DATE_RANGE_FILTER } from 'utils/applicationCreatedDateRangeFilter';
import {
  APPLICATION_LIST_APP_TYPE_FILTER_OPTIONS,
  APPLICATION_STAGE_FILTER_OPTIONS,
} from 'data/constants/common_constants';

const EMPTY_APPLICATION_LIST_FILTER = {
  searchText: '',
  options: {},
  selectedStage: [],
  selectedAppType: [],
  selectedLeaseDate: { startDate: null, endDate: null },
  selectedCreatedDate: { ...EMPTY_APPLICATION_CREATED_DATE_RANGE_FILTER },
  selectedTenantCount: [],
};

export const ApplicationTab = ({ building }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const prefilterRaw = searchParams.get('prefilter');
  const prefilterFromUrl = prefilterRaw ? prefilterRaw : null;
  const effectivePrefilter = prefilterFromUrl ?? null;
  const previousLandlordIdRef = useRef(undefined);

  const {
    filter,
    setFilter,
    sortOptions,
    setSortOptions,
    pagination,
    setPagination,
    extraFilters,
    setExtraFilters,
  } = useListQueryState({
    defaultFilter: {
      options: {},
      searchText: '',
      searchPlaceholder: 'Search Application..',
    },
    extraFiltersParam: 'filters',
    defaultExtraFilters: DEFAULT_APPLICATION_LIST_EXTRA_FILTERS,
    parseExtraFilters: parseApplicationsFilters,
    serializeExtraFilters: serializeApplicationsFilters,
  });

  const [selectedStage, setSelectedStage] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedStage),
  );
  const [selectedAppType, setSelectedAppType] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedAppType),
  );
  const [selectedLeaseDate, setSelectedLeaseDate] = useState(() =>
    normalizeLeaseDateFilter(extraFilters?.selectedLeaseDate),
  );
  const [selectedCreatedDate, setSelectedCreatedDate] = useState(() =>
    normalizeApplicationCreatedDateRangeFilter(extraFilters?.selectedCreatedDate),
  );
  const [selectedTenantCount, setSelectedTenantCount] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedTenantCount),
  );
  const [selectedStageDraft, setSelectedStageDraft] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedStage),
  );
  const [selectedAppTypeDraft, setSelectedAppTypeDraft] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedAppType),
  );
  const [selectedLeaseDateDraft, setSelectedLeaseDateDraft] = useState(() =>
    normalizeLeaseDateFilter(extraFilters?.selectedLeaseDate),
  );
  const [selectedCreatedDateDraft, setSelectedCreatedDateDraft] = useState(() =>
    normalizeApplicationCreatedDateRangeFilter(extraFilters?.selectedCreatedDate),
  );
  const [selectedTenantCountDraft, setSelectedTenantCountDraft] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedTenantCount),
  );
  const [fullExportDatasetTotal, setFullExportDatasetTotal] = useState(undefined);
  const [appliedPrefilterKey, setAppliedPrefilterKey] = useState(null);

  useEffect(() => {
    if (!extraFilters || typeof extraFilters !== 'object') return;
    const nextSelectedStage = normalizeArrayFilter(extraFilters.selectedStage);
    const nextSelectedAppType = normalizeArrayFilter(extraFilters.selectedAppType);
    const nextSelectedLeaseDate = normalizeLeaseDateFilter(extraFilters.selectedLeaseDate);
    const nextSelectedCreatedDate = normalizeApplicationCreatedDateRangeFilter(
      extraFilters.selectedCreatedDate,
    );
    const nextSelectedTenantCount = normalizeArrayFilter(extraFilters.selectedTenantCount);
    setSelectedStage((prev) =>
      areArraysEqual(prev, nextSelectedStage) ? prev : nextSelectedStage,
    );
    setSelectedAppType((prev) =>
      areArraysEqual(prev, nextSelectedAppType) ? prev : nextSelectedAppType,
    );
    setSelectedLeaseDate((prev) =>
      areDateRangesEqual(prev, nextSelectedLeaseDate) ? prev : nextSelectedLeaseDate,
    );
    setSelectedCreatedDate((prev) =>
      areApplicationCreatedDateRangeFiltersEqual(prev, nextSelectedCreatedDate)
        ? prev
        : nextSelectedCreatedDate,
    );
    setSelectedTenantCount((prev) =>
      areArraysEqual(prev, nextSelectedTenantCount) ? prev : nextSelectedTenantCount,
    );
    setSelectedStageDraft((prev) =>
      areArraysEqual(prev, nextSelectedStage) ? prev : nextSelectedStage,
    );
    setSelectedAppTypeDraft((prev) =>
      areArraysEqual(prev, nextSelectedAppType) ? prev : nextSelectedAppType,
    );
    setSelectedLeaseDateDraft((prev) =>
      areDateRangesEqual(prev, nextSelectedLeaseDate) ? prev : nextSelectedLeaseDate,
    );
    setSelectedCreatedDateDraft((prev) =>
      areApplicationCreatedDateRangeFiltersEqual(prev, nextSelectedCreatedDate)
        ? prev
        : nextSelectedCreatedDate,
    );
    setSelectedTenantCountDraft((prev) =>
      areArraysEqual(prev, nextSelectedTenantCount) ? prev : nextSelectedTenantCount,
    );
  }, [extraFilters]);

  const landlordScopeId = building?.sf_landlord_id || building?.landlord_id || building?.id || null;

  useLayoutEffect(() => {
    if (!landlordScopeId) return;
    if (previousLandlordIdRef.current !== landlordScopeId) {
      if (previousLandlordIdRef.current !== undefined) {
        const next = new URLSearchParams(searchParams);
        APPLICATION_LIST_URL_PARAM_KEYS_WITH_PREFILTER.forEach((key) => next.delete(key));
        setSearchParams(next, { replace: true });
        setAppliedPrefilterKey(null);
      }
      previousLandlordIdRef.current = landlordScopeId;
    }
  }, [landlordScopeId, searchParams, setSearchParams]);

  useLayoutEffect(() => {
    if (!landlordScopeId) return;

    if (!effectivePrefilter) {
      if (appliedPrefilterKey != null) {
        setAppliedPrefilterKey(null);
        setExtraFilters({ ...DEFAULT_APPLICATION_LIST_EXTRA_FILTERS });
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
      }
      return;
    }

    if (effectivePrefilter === appliedPrefilterKey) return;

    const prefilterPayload = getApplicationPrefilterByKey(effectivePrefilter);
    if (!prefilterPayload) {
      setAppliedPrefilterKey(effectivePrefilter);
      return;
    }

    const nextSelectedStage = prefilterPayload.selectedStage || [];
    const nextSelectedAppType = prefilterPayload.selectedAppType || [];
    setExtraFilters((prev) => ({
      ...prev,
      selectedStage: nextSelectedStage,
      selectedAppType: nextSelectedAppType,
    }));
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
    setAppliedPrefilterKey(effectivePrefilter);
  }, [effectivePrefilter, appliedPrefilterKey, setPagination, landlordScopeId, setExtraFilters]);

  const handleDispatch = useCallback(() => {
    dispatch(
      getUserAppAction({
        id: building.sf_landlord_id,
        role: 'll',

        filter: buildApplicationListRequestFilter({
          filter,
          selectedStage,
          selectedAppType,
          selectedLeaseDate,
          selectedCreatedDate,
          selectedTenantCount,
        }),
        sortOptions,
        offset: pagination.pageNumber * pagination.rowsPerPage,
        limit: pagination.rowsPerPage,
        buildingId: building.building_id,
      }),
    );
  }, [
    dispatch,
    building.building_id,
    building.sf_landlord_id,
    filter,
    selectedStage,
    selectedAppType,
    selectedLeaseDate,
    selectedCreatedDate,
    selectedTenantCount,
    sortOptions,
    pagination.pageNumber,
    pagination.rowsPerPage,
  ]);

  useEffect(() => {
    handleDispatch();
  }, [handleDispatch]);

  const applications = useSelector((state) => state.getIn(['property', 'application']));

  const resetFunc = useCallback(() => {
    setSelectedStage([]);
    setSelectedAppType([]);
    setSelectedLeaseDate({
      startDate: null,
      endDate: null,
    });
    setSelectedCreatedDate({ ...EMPTY_APPLICATION_CREATED_DATE_RANGE_FILTER });
    setSelectedTenantCount([]);
    setSelectedStageDraft([]);
    setSelectedAppTypeDraft([]);
    setSelectedLeaseDateDraft({
      startDate: null,
      endDate: null,
    });
    setSelectedCreatedDateDraft({ ...EMPTY_APPLICATION_CREATED_DATE_RANGE_FILTER });
    setSelectedTenantCountDraft([]);
    setExtraFilters({ ...DEFAULT_APPLICATION_LIST_EXTRA_FILTERS });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [setExtraFilters, setPagination]);

  const applyFunc = useCallback(() => {
    setSelectedStage(selectedStageDraft);
    setSelectedAppType(selectedAppTypeDraft);
    setSelectedLeaseDate(selectedLeaseDateDraft);
    setSelectedCreatedDate(selectedCreatedDateDraft);
    setSelectedTenantCount(selectedTenantCountDraft);
    setExtraFilters({
      selectedStage: selectedStageDraft,
      selectedAppType: selectedAppTypeDraft,
      selectedLeaseDate: selectedLeaseDateDraft,
      selectedCreatedDate: selectedCreatedDateDraft,
      selectedTenantCount: selectedTenantCountDraft,
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [
    selectedStageDraft,
    selectedAppTypeDraft,
    selectedLeaseDateDraft,
    selectedCreatedDateDraft,
    selectedTenantCountDraft,
    setExtraFilters,
    setPagination,
  ]);

  const STAGE = APPLICATION_STAGE_FILTER_OPTIONS;
  const APPTYPE = APPLICATION_LIST_APP_TYPE_FILTER_OPTIONS;

  const listFilterChips = useMemo(
    () =>
      buildApplicationListFilterChips({
        selectedStage,
        selectedAppType,
        selectedLeaseDate,
        selectedCreatedDate,
        selectedTenantCount,
        stageOptions: STAGE,
        appTypeOptions: APPTYPE,
      }),
    [
      selectedStage,
      selectedAppType,
      selectedLeaseDate,
      selectedCreatedDate,
      selectedTenantCount,
      STAGE,
      APPTYPE,
    ],
  );

  const handleRemoveListFilterChip = useCallback(
    (chipId) => {
      const parsed = parseApplicationListFilterChipId(chipId);
      if (!parsed) return;

      if (parsed.type === 'created') {
        const cleared = { ...EMPTY_APPLICATION_CREATED_DATE_RANGE_FILTER };
        setSelectedCreatedDate(cleared);
        setSelectedCreatedDateDraft(cleared);
        setExtraFilters((prev) => ({ ...prev, selectedCreatedDate: cleared }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'lease') {
        const cleared = { startDate: null, endDate: null };
        setSelectedLeaseDate(cleared);
        setSelectedLeaseDateDraft(cleared);
        setExtraFilters((prev) => ({ ...prev, selectedLeaseDate: cleared }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'stage') {
        const next = selectedStage.filter((s) => s !== parsed.value);
        setSelectedStage(next);
        setSelectedStageDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedStage: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'appType') {
        const next = selectedAppType.filter((s) => s !== parsed.value);
        setSelectedAppType(next);
        setSelectedAppTypeDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedAppType: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'tenant') {
        const next = selectedTenantCount.filter((s) => s !== parsed.value);
        setSelectedTenantCount(next);
        setSelectedTenantCountDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedTenantCount: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
      }
    },
    [selectedStage, selectedAppType, selectedTenantCount, setExtraFilters, setPagination],
  );

  const listFilterActiveSummary = useMemo(() => {
    if (!listFilterChips.length) return null;
    return (
      <ApplicationListActiveFilters
        chips={listFilterChips}
        onRemoveChip={handleRemoveListFilterChip}
        onClearAll={resetFunc}
      />
    );
  }, [listFilterChips, handleRemoveListFilterChip, resetFunc]);

  const onClickApplication = useCallback(
    (application) => {
      if (!application?.app_id) {
        dispatch(failedNotif('Application detail ID is missing.'));
        return;
      }
      const buildingParent = buildScopedBuildingBreadcrumbParent(building, {
        landlordId: building?.sf_landlord_id,
      });
      dispatch(
        openDetails({
          type: 'application',
          data: application,
        }),
      );
      navigate(`/property/applications/${application.app_id}/detail`, {
        state: withBreadcrumbParents(buildingParent ? [buildingParent] : []),
      });
    },
    [dispatch, navigate, building],
  );

  const applicationsJSON = applications && applications.toJS();
  useClampListPagination({
    pagination,
    setPagination,
    totalItems: Number(applicationsJSON?.total) || 0,
    isLoading: Boolean(applicationsJSON?.isLoading),
  });

  useEffect(() => {
    if (!building?.building_id || !building?.sf_landlord_id) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await getUserApp({
          id: building.sf_landlord_id,
          role: 'll',
          buildingId: building.building_id,
          filter: EMPTY_APPLICATION_LIST_FILTER,
          sortOptions,
          offset: 0,
          limit: 1,
        });
        if (!cancelled && response?.total != null) {
          setFullExportDatasetTotal(Number(response.total));
        }
      } catch (e) {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sortOptions, building?.building_id, building?.sf_landlord_id]);

  const handleRequestExportData = useCallback(async () => {
    const totalRecords = Number(applicationsJSON?.total) || 0;
    if (totalRecords === 0) return [];

    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getUserApp({
            id: building.sf_landlord_id,
            role: 'll',
            buildingId: building.building_id,
            filter: buildApplicationListRequestFilter({
              filter,
              selectedStage,
              selectedAppType,
              selectedLeaseDate,
              selectedCreatedDate,
              selectedTenantCount,
            }),
            sortOptions,
            offset,
            limit,
          }),
      });
    } catch (error) {
      throw new Error('Unable to export applications right now.');
    }
  }, [
    applicationsJSON?.total,
    filter,
    selectedStage,
    selectedAppType,
    selectedLeaseDate,
    selectedCreatedDate,
    selectedTenantCount,
    sortOptions,
    building.building_id,
    building.sf_landlord_id,
  ]);

  const handleRequestFullExportData = useCallback(async () => {
    const totalRecords = Number(fullExportDatasetTotal) || Number(applicationsJSON?.total) || 0;
    if (totalRecords === 0) return [];

    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getUserApp({
            id: building.sf_landlord_id,
            role: 'll',
            buildingId: building.building_id,
            filter: EMPTY_APPLICATION_LIST_FILTER,
            sortOptions,
            offset,
            limit,
          }),
      });
    } catch (error) {
      throw new Error('Unable to export applications right now.');
    }
  }, [
    fullExportDatasetTotal,
    applicationsJSON?.total,
    sortOptions,
    building.building_id,
    building.sf_landlord_id,
  ]);

  return (
    <Box>
      {applications ? (
        <React.Fragment>
          <CustomBox>
            {applicationsJSON.isLoading && (
              <LoadingEffect>
                <Loading size={'large'} />
              </LoadingEffect>
            )}
          </CustomBox>
          <ApplicationTable
            applications={applicationsJSON.data}
            onRequestExportData={handleRequestExportData}
            onRequestFullExportData={handleRequestFullExportData}
            exportFullDatasetTotal={fullExportDatasetTotal}
            listFilterActiveSummary={listFilterActiveSummary}
            listFilterActiveCount={listFilterChips.length}
            shouldShowBuildingName={false}
            filter={filter}
            onChangeFilter={setFilter}
            pagination={{
              ...pagination,
              totalItems: applicationsJSON.total,
              totalPages: Math.ceil(applicationsJSON.total / pagination.rowsPerPage),
            }}
            dropFilter={{
              component: (
                <ApplicationListDropFilterFields
                  stageOptions={STAGE}
                  appTypeOptions={APPTYPE}
                  selectedStageDraft={selectedStageDraft}
                  onStageDraftChange={setSelectedStageDraft}
                  selectedAppTypeDraft={selectedAppTypeDraft}
                  onAppTypeDraftChange={setSelectedAppTypeDraft}
                  selectedLeaseDateDraft={selectedLeaseDateDraft}
                  onLeaseDateDraftChange={setSelectedLeaseDateDraft}
                  selectedCreatedDateDraft={selectedCreatedDateDraft}
                  onCreatedDateDraftChange={setSelectedCreatedDateDraft}
                  selectedTenantCountDraft={selectedTenantCountDraft}
                  onTenantCountDraftChange={setSelectedTenantCountDraft}
                />
              ),
              resetFunc: resetFunc,
              applyFunc: applyFunc,
            }}
            onChangePagination={setPagination}
            sortOptions={sortOptions}
            onChangeSort={setSortOptions}
            // shouldShowBuildingName={false}
            onClickApplication={onClickApplication}
          />
        </React.Fragment>
      ) : (
        <Loading size={'large'} label={''} />
      )}
    </Box>
  );
};

const CustomBox = styled.div`
  position: relative;
`;

const LoadingEffect = styled.div`
  position: absolute;
  top: 35%;
  left: 45%;
  z-index: 1;
`;
